import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLeadership } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import ProfileCarousel from '../components/ProfileCarousel';

const About: React.FC = () => {
  const { t } = useTranslation();
  const { locale } = useLanguage();
  const [leadership, setLeadership] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeadership = async () => {
      try {
        const data = await getLeadership(locale);
        setLeadership(data);
      } catch (error) {
        console.error("Failed to fetch leadership:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchLeadership();
  }, [locale]);

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-700">
      {/* Narrative Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="block">
            <div className="space-y-8">
              <div className="inline-flex items-center space-x-2 text-brand-purple-500 font-bold text-xs uppercase tracking-widest">
                <span className="w-8 h-px bg-brand-purple-500"></span>
                <span>{t('nav.about')}</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-serif font-bold text-brand-purple-900 leading-tight">TJA</h1>
              <div className="text-base lg:text-lg text-gray-600 leading-relaxed lg:leading-loose font-light rtl:text-right block">
                <div className="relative float-right ml-8 mb-6 w-full lg:w-[500px] h-auto rounded-lg rtl:float-left rtl:mr-8 rtl:ml-0 z-10">
                  <div className="absolute inset-0 bg-brand-yellow-400 rounded-lg rotate-3 opacity-20"></div>
                  <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-brand-green-500 rounded-lg z-0 opacity-10"></div>
                  <img
                    src="/images/about.jpg"
                    alt="TJA Dayanışma Grubu"
                    className="relative z-10 w-full h-auto object-cover rounded-lg shadow-xl"
                  />
                </div>
                {Array.isArray(t('about.manifesto', { returnObjects: true })) && 
                  (t('about.manifesto', { returnObjects: true }) as string[]).map((paragraph, idx) => (
                  <p key={idx} className="mb-6">{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-32 bg-brand-neutral-bg relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-purple-100/30 rounded-full blur-[120px] -translate-y-1/2"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20 space-y-4">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-brand-purple-900">{t('about.values_title')}</h2>
            <p className="text-gray-500 max-w-xl mx-auto">{t('about.values_subtitle')}</p>
            <div className="w-20 h-1.5 bg-brand-green-500 mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {[
              { title: t('about.value1_title'), desc: t('about.value1_desc') },
              { title: t('about.value2_title'), desc: t('about.value2_desc') },
              { title: t('about.value3_title'), desc: t('about.value3_desc') },
              { title: t('about.value4_title'), desc: t('about.value4_desc') }
            ].map((value, idx) => (
              <div key={idx} className="group bg-white p-10 rounded-[2.5rem] shadow-sm border border-brand-purple-50 hover:shadow-2xl hover:shadow-brand-purple-900/5 transition-all duration-500 hover:-translate-y-2">
                <h3 className="text-2xl font-serif font-bold text-brand-purple-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed font-light">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      {loading ? (
        <div className="flex justify-center py-32 bg-white">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-purple-900"></div>
        </div>
      ) : (
        <ProfileCarousel 
          profiles={leadership} 
          title={t('about.leadership_title') as string} 
          quote={t('about.leadership_quote') as string} 
          className="bg-white py-12 md:py-24"
        />
      )}

    </div>
  );
};

export default About;
